import { TrendingUp, Shield, Clock, Eye, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import CampaignBudgetBar from '@/components/CampaignBudgetBar';
import { CampaignAvatar } from '@/components/campaigns/CampaignAvatar';
import { isCampaignBudgetExhausted } from '@/lib/campaigns/isCampaignBudgetExhausted';
import { RankingCampaignSummary } from '@/components/campaigns/RankingCampaignSummary';
import { CampaignCpmRateList } from '@/components/campaigns/CampaignCpmRateList';
import { describeCampaignCpmRates } from '@/lib/campaigns/describeCampaignCpmRates';

 
type Campaign = any;

interface Props {
  campaign: Campaign | null | undefined;
}

export default function CampaignHero({ campaign }: Props) {
  const { t } = useTranslation();
  if (!campaign) return null;
  if (campaign.campaign_type === 'ranking') return <Card><CardContent className="space-y-3 p-6"><h2 className="text-xl font-semibold">{campaign.name}</h2><RankingCampaignSummary config={campaign.ranking_config} currency={campaign.currency || 'BRL'} /></CardContent></Card>;

  const currency = campaign.currency || 'BRL';
  const formatEarnings = (cents: number) => {
    const value = (cents || 0) / 100;
    return new Intl.NumberFormat(currency === 'BRL' ? 'pt-BR' : 'en-US', {
      style: 'currency',
      currency,
    }).format(value);
  };

  const heroRates = describeCampaignCpmRates(campaign);
  const total = campaign.budget_total_cents || 0;
  const remaining =
    campaign.budget_remaining_cents
    ?? Math.max(0, total - (campaign.budget_spent_cents || 0));
  const spent = Math.max(0, total - remaining);
  const pending = campaign.budget_pending_cents || 0;
  const spendDriftCents = Number.isInteger(campaign.budget_spend_drift_cents)
    ? campaign.budget_spend_drift_cents
    : 0;
  const budgetExhausted = isCampaignBudgetExhausted({
    remainingCents: remaining,
    totalCents: total,
  });
  const budgetLow = !budgetExhausted && total > 0 && remaining / total < 0.25;
  const isActive = campaign.status === 'active';
  const isEnded = campaign.status === 'ended';

  return (
    <Card className="bg-gradient-to-br from-emerald-500/5 via-card to-card border-border overflow-hidden">
      <CardContent className="p-6 sm:p-8">
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div className="flex items-start gap-4 min-w-0 flex-1">
            <CampaignAvatar campaign={campaign} size="lg" />
            <div className="min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-2xl sm:text-3xl font-bold truncate">
                  {campaign.name}
                </h2>
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border flex-shrink-0 ${
                    isActive
                      ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/40'
                      : isEnded
                        ? 'bg-muted text-muted-foreground border-border'
                        : 'bg-amber-500/10 text-amber-400 border-amber-500/40'
                  }`}
                >
                  {t(`campaignAudit.status.${campaign.status}`)}
                </span>
              </div>
              {campaign.description && (
                <p className="text-muted-foreground mt-1">{campaign.description}</p>
              )}
            </div>
          </div>

          {campaign.campaign_type === 'ranking' ? <RankingCampaignSummary config={campaign.ranking_config} currency={currency} /> : <div className="flex gap-3 flex-wrap">
            <div className="bg-background/60 border border-border rounded-xl px-4 py-3 flex items-center gap-3 min-w-[11rem]">
              <div className="w-9 h-9 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  {t('campaign.rateLabel')}
                </p>
                {heroRates.kind === 'single' ? (
                  <p className="font-bold leading-tight">
                    {formatEarnings(heroRates.rateCents)}
                    <span className="text-xs text-muted-foreground font-normal">
                      {' / '}
                      {t('campaign.per1kViews')}
                    </span>
                  </p>
                ) : (
                  <CampaignCpmRateList rates={heroRates.rates} currency={currency} />
                )}
              </div>
            </div>

            {campaign.min_views_threshold ? (
              <div className="bg-background/60 border border-border rounded-xl px-4 py-3 flex items-center gap-3 min-w-[11rem]">
                <div className="w-9 h-9 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
                  <Eye className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    {t('campaign.minViewsLabel')}
                  </p>
                  <p className="font-bold leading-tight">
                    {Number(campaign.min_views_threshold).toLocaleString()}
                  </p>
                </div>
              </div>
            ) : null}

            {campaign.max_payout_per_clip_cents ? (
              <div className="bg-background/60 border border-border rounded-xl px-4 py-3 flex items-center gap-3 min-w-[11rem]">
                <div className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    {t('campaign.clipLimitLabel')}
                  </p>
                  <p className="font-bold leading-tight">
                    {formatEarnings(campaign.max_payout_per_clip_cents)}
                  </p>
                </div>
              </div>
            ) : null}
          </div>}
        </div>

        <div className="mt-8 pt-6 border-t border-border space-y-3">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-muted-foreground">
                {t('campaign.budgetSectionTitle')}
              </span>
              {budgetExhausted && (
                <span
                  data-testid="campaign-budget-exhausted-badge"
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 text-[10px] font-bold uppercase tracking-wider"
                >
                  <AlertTriangle className="w-2.5 h-2.5" />
                  {t('campaign.budgetExhausted')}
                </span>
              )}
              {budgetLow && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 text-[10px] font-bold uppercase tracking-wider">
                  <Clock className="w-2.5 h-2.5" />
                  {t('campaign.budgetLow')}
                </span>
              )}
            </div>
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              {t('campaign.totalSpent')}
            </span>
          </div>

          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <span className={`text-3xl sm:text-4xl font-bold ${budgetExhausted ? 'text-red-400' : ''}`}>
                {formatEarnings(remaining)}
              </span>
              <span className="ml-2 text-muted-foreground">
                {t('campaign.remaining')}
              </span>
            </div>
            <span className="text-xl sm:text-2xl font-bold text-emerald-400">
              {formatEarnings(spent)}
            </span>
          </div>

          <div className="space-y-1">
            <CampaignBudgetBar
              totalCents={total}
              spentCents={spent}
              pendingCents={pending}
              currency={currency}
              size="md"
              showLegend
            />
            <div className="flex items-center justify-between text-[11px] text-muted-foreground">
              <span>{formatEarnings(0)}</span>
              <span>
                {t('campaign.budgetGoal', {
                  amount: formatEarnings(total),
                })}
              </span>
            </div>
            {budgetExhausted && (
              <p
                data-testid="campaign-budget-exhausted-banner"
                className="text-sm text-red-300"
              >
                {t('campaign.budgetExhaustedBanner')}
              </p>
            )}
            {spendDriftCents !== 0 && (
              <p
                data-testid="campaign-budget-spend-drift"
                className="text-sm text-amber-300"
              >
                {spendDriftCents > 0
                  ? t('campaign.budgetSpendDrift', {
                    stored: formatEarnings(campaign.budget_stored_spent_cents || 0),
                    approved: formatEarnings(campaign.budget_spent_cents || 0),
                    amount: formatEarnings(spendDriftCents),
                  })
                  : t('campaign.budgetSpendDriftBehind', {
                    stored: formatEarnings(campaign.budget_stored_spent_cents || 0),
                    approved: formatEarnings(campaign.budget_spent_cents || 0),
                  })}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
